import { Component, EventEmitter, Injector, Input, OnInit, Output } from '@angular/core';
import { PaymentService } from 'app/core/http/services/payment.service';
import { Encounter } from 'app/core/interfaces/encounter';
import { Payment } from 'app/core/interfaces/payment';
import { BaseComponent } from 'app/modules/shared/components/base.component';
import { getHttpErrorMessage } from 'app/core/utils/http-error-message';

@Component({
  selector: 'src-payment-physical',
  templateUrl: './payment-physical.component.html',
  styleUrls: ['./payment-physical.component.css'],
})
export class PaymentPhysicalComponent extends BaseComponent implements OnInit {
  @Input() encounter!: Encounter;
  public submitted = false;

  @Output() onConfirmPayment = new EventEmitter<Payment>();

  public confirmPayment: boolean = false;
  public userType: string = '';
  public paymentErrorMessage = 'Falha ao confirmar pagamento. Tente novamente ou entre em contato com o suporte!';

  constructor(
    private paymentService: PaymentService,
    private injector: Injector,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.userType = this.currentUser?.type || localStorage.getItem('userType') || '';
  }

  createPayment() {
    if (!this.canConfirmPhysicalPayment()) {
      return;
    }

    if (this.submitted || this.showSuccessMessage) {
      return;
    }

    if (!this.confirmPayment) {
      this.paymentErrorMessage = 'Confirme que o pagamento presencial foi recebido.';
      this.showErrorMessage = true;
      return;
    }

    const customerInfo = this.getCustomerInfo();
    const missingFields = this.getMissingCustomerFields(customerInfo);

    if (missingFields.length > 0) {
      this.paymentErrorMessage = `Dados do paciente incompletos para confirmar pagamento: ${missingFields.join(', ')}.`;
      this.showErrorMessage = true;
      return;
    }

    this.submitted = true;

    const payment: Payment = {
      encounter_id: this.encounter.encounter_id,
      customer_info: customerInfo,
      payment_method: 'PHYSICAL',
      confirmed_by: this.getConfirmedBy(),
    };

    this.paymentService.create(payment).subscribe({
      next: (payment: Payment) => {
        this.showSuccessMessage = true;
        this.submitted = false;

        this.onConfirmPayment.emit(payment);
      },
      error: (err) => {
        this.paymentErrorMessage = getHttpErrorMessage(err, 'Falha ao confirmar pagamento. Tente novamente ou entre em contato com o suporte!');
        this.showErrorMessage = true;
        this.submitted = false;
      },
    });
  }

  canConfirmPhysicalPayment(): boolean {
    return this.userType === 'secretary' || this.userType === 'organization';
  }

  canSubmitPhysicalPayment(): boolean {
    return this.canConfirmPhysicalPayment() && !!this.confirmPayment && !this.showSuccessMessage && !this.submitted;
  }

  private getConfirmedBy(): string {
    const user: any = this.currentUser?.user || {};
    return user.id || user.secretary_id || user.organization_id || '';
  }

  private getCustomerInfo(): any {
    const patientDetails = this.encounter?.patient_details || {};
    const patient: any = this.encounter?.patient || {};

    return {
      ...patientDetails,
      patient_id: patientDetails.patient_id || patient.patient_id || '',
      name: patientDetails.name || patient.name || '',
      email: patientDetails.email || patient.email || '',
      cpfCnpj: this.onlyNumbers(
        patientDetails.cpfCnpj || patientDetails.cpf || patientDetails.document || patient.cpfCnpj || patient.cpf || patient.document || '',
      ),
      document: this.onlyNumbers(
        patientDetails.document || patientDetails.cpfCnpj || patientDetails.cpf || patient.document || patient.cpfCnpj || patient.cpf || '',
      ),
      phone: this.normalizePhone(patientDetails.phone || patientDetails.mobile || patient.phone || patient.mobile || ''),
    };
  }

  private getMissingCustomerFields(customerInfo: any): string[] {
    const missingFields: string[] = [];

    if (!customerInfo.name) {
      missingFields.push('nome');
    }

    if (!customerInfo.email) {
      missingFields.push('email');
    }

    if (!customerInfo.document && !customerInfo.cpfCnpj) {
      missingFields.push('documento');
    }

    if (!customerInfo.phone) {
      missingFields.push('telefone');
    }

    return missingFields;
  }

  private onlyNumbers(value: string): string {
    return String(value || '').replace(/\D/g, '');
  }

  private normalizePhone(value: string): string {
    const phone = this.onlyNumbers(value);

    if (phone.startsWith('55') && phone.length > 11) {
      return phone.substring(2);
    }

    return phone;
  }
}
